import React, { useState, useEffect } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, TextField,
  Switch, FormControlLabel, Select, MenuItem, InputLabel, FormControl,
  Slider, Divider, Alert, Tooltip, Chip, InputAdornment,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { botsApi } from "../../../api/bots.js";
import { useTheme } from "@mui/material";
import {
  EMBED_FONTS, WIDGET_THEME_PRESETS,
  type EmbedConfig, type ChatWidgetTheme, type Bot,
} from "@chatfab/shared";

const COLOR_PRESETS = [
  { name: "Default", color: "#5C6BC0" },
  { name: "Ocean",   color: "#2196F3" },
  { name: "Emerald", color: "#10B981" },
  { name: "Rose",    color: "#E11D48" },
  { name: "Violet",  color: "#7C3AED" },
  { name: "Slate",   color: "#475569" },
];

const RADIUS_PRESETS = [
  { label: "Sharp",   value: 0  },
  { label: "Subtle",  value: 8  },
  { label: "Rounded", value: 14 },
  { label: "Pill",    value: 24 },
];

const DEFAULT_WIDGET_THEME: ChatWidgetTheme = {
  primary_color:    "#5C6BC0",
  background_color: "#FFFFFF",
  font_family:      "Inter",
  border_radius:    10,
  preset_name:      "Default",
  dark_mode:        false,
};

const DARK_BG  = "#1C1E2B";
const LIGHT_BG = "#FFFFFF";

interface Props { botId: string; bot: Bot; onUpdate: () => void }

export function EmbedTab({ botId, bot, onUpdate }: Props) {
  const muiTheme = useTheme();
  const [config, setConfig]       = useState<EmbedConfig>(bot.embed_config);
  const [snippet, setSnippet]     = useState<string>("");
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [copied, setCopied]       = useState(false);
  const [widgetTheme, setWidgetTheme] = useState<ChatWidgetTheme>(
    bot.embed_config.widget_theme ?? DEFAULT_WIDGET_THEME,
  );

  useEffect(() => {
    botsApi.getEmbed(botId).then(r => { setConfig(r.embed_config); setSnippet(r.snippet); setWidgetTheme(r.embed_config.widget_theme ?? DEFAULT_WIDGET_THEME); });
  }, [botId]);

  // Dynamically load the selected font for the live preview
  useEffect(() => {
    const font = widgetTheme.font_family;
    if (font === "Inter") return; // already loaded globally
    const id = `preview-font-${font.replace(/\s+/g, "-")}`;
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }, [widgetTheme.font_family]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await botsApi.updateEmbed(botId, { ...config, widget_theme: widgetTheme });
      setSnippet(res.snippet);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onUpdate();
    } finally { setSaving(false); }
  };

  const applyPreset = (name: string) => {
    const p = WIDGET_THEME_PRESETS[name];
    if (p) setWidgetTheme({ ...p, preset_name: name });
  };

  const copy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Embed Settings</Typography>
        <Stack direction="row" spacing={1.5}>
          {saved && <Alert severity="success" sx={{ py: 0.5 }}>Saved!</Alert>}
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* Left column — config */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>

            {/* General */}
            <Card>
              <CardContent>
                <Typography variant="h5" mb={2}>General</Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={<Switch checked={config.enabled} onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} color="primary" />}
                    label="Enable embed widget"
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Position</InputLabel>
                    <Select label="Position" value={config.position} onChange={e => setConfig(c => ({ ...c, position: e.target.value as any }))}>
                      <MenuItem value="bottom-right">Bottom Right</MenuItem>
                      <MenuItem value="bottom-left">Bottom Left</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Welcome message" fullWidth size="small"
                    value={config.welcome_message} onChange={e => setConfig(c => ({ ...c, welcome_message: e.target.value }))}
                  />
                  <TextField
                    label="Allowed domains" fullWidth size="small" multiline rows={2}
                    value={config.allowed_domains.join("\n")}
                    onChange={e => setConfig(c => ({ ...c, allowed_domains: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) }))}
                    helperText="One domain per line. Leave empty to allow all domains."
                    placeholder="example.com&#10;docs.example.com"
                  />
                  <FormControlLabel
                    control={<Switch checked={config.show_branding} onChange={e => setConfig(c => ({ ...c, show_branding: e.target.checked }))} />}
                    label="Show 'Powered by ChatFab' branding"
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Widget theme */}
            <Card>
              <CardContent>
                <Typography variant="h5" mb={0.5}>Widget Theme</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Customize colors, fonts and shape to match your website.
                </Typography>

                {/* Color presets */}
                <Typography variant="overline" mb={1} display="block">Color Preset</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2.5} useFlexGap>
                  {COLOR_PRESETS.map(p => (
                    <Tooltip key={p.name} title={p.name}>
                      <Box
                        onClick={() => applyPreset(p.name)}
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5,
                          bgcolor: p.color, cursor: "pointer",
                          border: widgetTheme.preset_name === p.name ? `3px solid ${muiTheme.palette.text.primary}` : "3px solid transparent",
                          boxShadow: widgetTheme.preset_name === p.name ? `0 0 0 1px ${muiTheme.palette.divider}` : "none",
                          transition: "all 150ms",
                          "&:hover": { transform: "scale(1.1)" },
                        }}
                      />
                    </Tooltip>
                  ))}
                  {/* Custom color */}
                  <Tooltip title="Custom color">
                    <Box sx={{ position: "relative", width: 32, height: 32 }}>
                      <Box
                        sx={{
                          width: 32, height: 32, borderRadius: 1.5, cursor: "pointer",
                          background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                          border: widgetTheme.preset_name === "Custom" ? `3px solid ${muiTheme.palette.text.primary}` : `3px solid ${muiTheme.palette.divider}`,
                        }}
                      />
                      <input
                        type="color" value={widgetTheme.primary_color}
                        onChange={e => setWidgetTheme(t => ({ ...t, primary_color: e.target.value, preset_name: "Custom" }))}
                        style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                      />
                    </Box>
                  </Tooltip>
                </Stack>

                {/* Hex input */}
                <TextField
                  label="Primary color (hex)" size="small" sx={{ mb: 2.5, width: 200 }}
                  value={widgetTheme.primary_color}
                  onChange={e => {
                    const v = e.target.value;
                    setWidgetTheme(t => ({ ...t, primary_color: v, preset_name: "Custom" }));
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: widgetTheme.primary_color, border: `1px solid ${muiTheme.palette.divider}` }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Dark mode toggle */}
                <FormControlLabel
                  sx={{ mb: 2.5 }}
                  control={
                    <Switch
                      checked={widgetTheme.dark_mode ?? false}
                      onChange={e => {
                        const on = e.target.checked;
                        setWidgetTheme(t => ({
                          ...t,
                          dark_mode: on,
                          // Auto-set a sensible background when toggling
                          background_color: on
                            ? (t.background_color === LIGHT_BG ? DARK_BG : t.background_color)
                            : (t.background_color === DARK_BG  ? LIGHT_BG : t.background_color),
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label="Dark mode widget"
                />

                {/* Background color */}
                <TextField
                  label="Background color (hex)" size="small" sx={{ mb: 2.5, width: 200 }}
                  value={widgetTheme.background_color}
                  onChange={e => setWidgetTheme(t => ({ ...t, background_color: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ position: "relative", width: 16, height: 16 }}>
                          <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: widgetTheme.background_color, border: `1px solid ${muiTheme.palette.divider}` }} />
                          <input
                            type="color" value={widgetTheme.background_color}
                            onChange={e => setWidgetTheme(t => ({ ...t, background_color: e.target.value }))}
                            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                          />
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />

                <Divider sx={{ mb: 2.5 }} />

                {/* Font */}
                <Typography variant="overline" mb={1} display="block">Font</Typography>
                <Grid container spacing={1} mb={2.5}>
                  {EMBED_FONTS.map(f => (
                    <Grid item key={f}>
                      <Chip
                        label={f}
                        onClick={() => setWidgetTheme(t => ({ ...t, font_family: f }))}
                        variant={widgetTheme.font_family === f ? "filled" : "outlined"}
                        color={widgetTheme.font_family === f ? "primary" : "default"}
                        size="small"
                        sx={{ fontFamily: f, cursor: "pointer" }}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ mb: 2.5 }} />

                {/* Border radius */}
                <Typography variant="overline" mb={1} display="block">Corner Style</Typography>
                <Stack direction="row" spacing={1} mb={1}>
                  {RADIUS_PRESETS.map(r => (
                    <Button
                      key={r.label} size="small"
                      variant={widgetTheme.border_radius === r.value ? "contained" : "outlined"}
                      onClick={() => setWidgetTheme(t => ({ ...t, border_radius: r.value }))}
                      sx={{ borderRadius: `${r.value}px`, minWidth: 80 }}
                    >
                      {r.label}
                    </Button>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Custom: {widgetTheme.border_radius}px
                </Typography>
                <Slider
                  value={widgetTheme.border_radius} min={0} max={24} step={1}
                  onChange={(_, v) => setWidgetTheme(t => ({ ...t, border_radius: v as number }))}
                  sx={{ mt: 0.5 }}
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right column — preview + snippet */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3} sx={{ position: "sticky", top: 88 }}>
            {/* Widget preview */}
            <Card>
              <CardContent>
                <Typography variant="h5" mb={2}>Preview</Typography>
                <Box sx={{
                  bgcolor: widgetTheme.dark_mode ? "#0D1117" : "#f0f2f5",
                  borderRadius: 2, p: 2, minHeight: 320,
                  display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
                  position: "relative",
                }}>
                  {/* Simulated chat bubble */}
                  <Box sx={{
                    width: 56, height: 56, borderRadius: `${Math.min(widgetTheme.border_radius + 8, 28)}px`,
                    bgcolor: widgetTheme.primary_color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)", cursor: "pointer",
                    color: "#fff", fontSize: 22,
                  }}>
                    💬
                  </Box>

                  {/* Simulated open panel */}
                  <Box sx={{
                    position: "absolute", bottom: 80, right: 8,
                    width: 280, bgcolor: widgetTheme.background_color,
                    borderRadius: `${widgetTheme.border_radius}px`,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                    fontFamily: widgetTheme.font_family,
                  }}>
                    {/* Header */}
                    <Box sx={{ bgcolor: widgetTheme.primary_color, p: 1.5 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 14, fontFamily: widgetTheme.font_family }}>
                        {bot.name}
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontFamily: widgetTheme.font_family }}>
                        {config.welcome_message || "Hi! How can I help?"}
                      </Typography>
                    </Box>
                    {/* Messages */}
                    <Box sx={{ p: 1.5 }}>
                      <Box sx={{ bgcolor: widgetTheme.dark_mode ? "rgba(255,255,255,0.09)" : "#f0f2f5", borderRadius: `${Math.max(widgetTheme.border_radius - 2, 4)}px`, p: 1, mb: 1, maxWidth: "80%" }}>
                        <Typography sx={{ fontSize: 12, fontFamily: widgetTheme.font_family, color: widgetTheme.dark_mode ? "#E8EAF0" : "inherit" }}>Hello! Ask me anything.</Typography>
                      </Box>
                      <Box sx={{ bgcolor: widgetTheme.primary_color, borderRadius: `${Math.max(widgetTheme.border_radius - 2, 4)}px`, p: 1, maxWidth: "80%", ml: "auto" }}>
                        <Typography sx={{ fontSize: 12, color: "#fff", fontFamily: widgetTheme.font_family }}>How does this work?</Typography>
                      </Box>
                    </Box>
                    {/* Input bar */}
                    <Box sx={{ borderTop: `1px solid ${widgetTheme.dark_mode ? "rgba(255,255,255,0.10)" : "#eee"}`, p: 1, display: "flex", gap: 0.5 }}>
                      <Box sx={{ flex: 1, bgcolor: widgetTheme.dark_mode ? "rgba(255,255,255,0.06)" : "#f5f5f5", borderRadius: `${Math.max(widgetTheme.border_radius - 4, 4)}px`, px: 1.5, py: 0.75 }}>
                        <Typography sx={{ fontSize: 12, color: widgetTheme.dark_mode ? "rgba(255,255,255,0.35)" : "#aaa", fontFamily: widgetTheme.font_family }}>Type a message…</Typography>
                      </Box>
                      <Box sx={{ width: 28, height: 28, borderRadius: `${Math.max(widgetTheme.border_radius - 4, 4)}px`, bgcolor: widgetTheme.primary_color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                        ↑
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Embed snippet */}
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="h5">Embed Snippet</Typography>
                  <Button size="small" startIcon={<ContentCopyIcon />} onClick={copy}>
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </Stack>
                <Box sx={{
                  bgcolor: "#1C1E2B", borderRadius: 1.5, p: 2,
                  fontFamily: "monospace", fontSize: 12,
                  color: "#E8EAF0", whiteSpace: "pre-wrap", wordBreak: "break-all",
                  maxHeight: 180, overflow: "auto",
                }}>
                  {snippet || "Save your settings to generate the snippet."}
                </Box>
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  Paste this before the closing &lt;/body&gt; tag on your website.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
